import { theme, FONT_RUNE } from "./theme";
import { useGame } from "./store";
import type { Capture, Side } from "../engine";

export function VictoryScreen({ onExit, onRematch, onDismiss }: { onExit: () => void; onRematch: () => void; onDismiss?: () => void }) {
  const v = useGame((s) => s.game.victory);
  const history = useGame((s) => s.game.history);
  if (!v) return null;

  // Flatten history into a chronological list of captures with the side that took each.
  const log: Array<{ turn: number; side: Side; capture: Capture }> = [];
  history.forEach((turn, i) => {
    for (const c of turn.captures) {
      log.push({ turn: i + 1, side: turn.side, capture: c });
    }
  });

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: "rgba(10, 6, 4, 0.85)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 50,
        backdropFilter: "blur(2px)",
        padding: 16,
      }}
    >
      <div
        style={{
          background: theme.bgElev,
          border: `1px solid ${theme.accent}`,
          borderRadius: 10,
          padding: "24px 22px",
          maxWidth: 460,
          width: "100%",
          maxHeight: "calc(100vh - 32px)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-script)",
            color: theme.accentSoft,
            fontSize: 56,
            lineHeight: 1,
            marginBottom: 2,
            textAlign: "center",
          }}
        >
          Victory
        </div>
        <div
          className="rune"
          style={{ color: theme.parchment, fontSize: 28, marginBottom: 8, textAlign: "center" }}
        >
          {v.side === "white" ? "White" : "Black"} prevails
        </div>
        <div
          className="rune"
          style={{ color: theme.accent, fontSize: 18, marginBottom: 14, textAlign: "center" }}
        >
          {labelForLevel(v.level)}
        </div>
        <p
          style={{
            color: theme.parchment,
            fontSize: 14,
            lineHeight: 1.5,
            opacity: 0.85,
            textAlign: "center",
            margin: 0,
          }}
        >
          {v.detail}
        </p>

        {log.length > 0 && (
          <div
            style={{
              marginTop: 16,
              padding: "12px 0",
              borderTop: `1px solid ${theme.boardEdge}`,
              borderBottom: `1px solid ${theme.boardEdge}`,
              overflowY: "auto",
              minHeight: 0,
            }}
          >
            <div
              className="rune"
              style={{
                fontSize: 11,
                letterSpacing: 2,
                color: theme.boardLabel,
                marginBottom: 8,
              }}
            >
              CAPTURE REVIEW · {log.length} TOTAL
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {log.map((entry, i) => (
                <CaptureLogRow key={i} {...entry} />
              ))}
            </div>
          </div>
        )}

        <div style={{ marginTop: 16, display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
          <button onClick={onExit} style={btn}>
            Menu
          </button>
          {onDismiss && (
            <button onClick={onDismiss} style={btn}>
              View board
            </button>
          )}
          <button onClick={onRematch} style={btnPrimary}>
            Rematch
          </button>
        </div>
      </div>
    </div>
  );
}

function CaptureLogRow({ turn, side, capture }: { turn: number; side: Side; capture: Capture }) {
  const sideLabel = side === "white" ? "W" : "B";
  const sideColor = side === "white" ? theme.whiteFill : theme.blackEdge;
  const sideBg = side === "white" ? theme.whiteEdge : theme.blackFill;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontSize: 12,
        padding: "4px 8px",
        background: theme.bg,
        borderRadius: 4,
        border: `1px solid ${theme.boardEdge}`,
      }}
    >
      <span style={{ color: theme.boardLabel, fontSize: 11, minWidth: 24 }}>#{turn}</span>
      <span
        className="rune"
        style={{
          background: sideBg,
          color: sideColor,
          padding: "1px 5px",
          borderRadius: 3,
          fontSize: 10,
          letterSpacing: 1,
          minWidth: 18,
          textAlign: "center",
        }}
      >
        {sideLabel}
      </span>
      <span
        className="rune"
        style={{ color: theme.accentSoft, fontSize: 11, letterSpacing: 1, minWidth: 72 }}
      >
        {capture.method.toUpperCase()}
      </span>
      <span style={{ color: theme.parchment, fontFamily: FONT_RUNE, fontSize: 13, opacity: 0.92 }}>
        {capture.formula}
      </span>
    </div>
  );
}

function labelForLevel(l: string): string {
  switch (l) {
    case "common":
      return "Common Victory";
    case "proper":
      return "Proper Victory";
    case "greater":
      return "Greater Victory";
    case "royal":
      return "Royal Victory";
    case "glorious":
      return "Glorious Victory";
    default:
      return l;
  }
}

const btn: React.CSSProperties = {
  fontFamily: FONT_RUNE,
  fontSize: 14,
  background: theme.boardEdge,
  color: theme.parchment,
  padding: "8px 18px",
  borderRadius: 5,
  border: `1px solid ${theme.inkSoft}`,
  cursor: "pointer",
};

const btnPrimary: React.CSSProperties = {
  ...btn,
  background: theme.accent,
  color: theme.bg,
  borderColor: theme.accent,
};
